import { memo, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { freeWordCorrect } from '../engine/session';

/**
 * Strict display: the target text as units (a char in English, a syllable in Bijoy).
 * Finished units are green, the current one carries the caret, the rest are grey.
 * A unit that has ever been mistyped keeps a faint red underline.
 */
export const StrictRibbon = memo(function StrictRibbon({ session, shake, size = 30 }) {
    const { units, index, unitErrors } = session;
    const words = useMemo(() => {
        const groups = [];
        units.forEach((unit, i) => {
            if (unit.space) { groups.push({ space: true, i }); return; }
            const last = groups[groups.length - 1];
            if (last && last.wordIndex === unit.wordIndex && !last.space) last.units.push({ unit, i });
            else groups.push({ wordIndex: unit.wordIndex, units: [{ unit, i }] });
        });
        return groups;
    }, [units]);

    return (
        <div
            dir="ltr"
            className={`typing-text font-hind leading-[1.7] tracking-wide text-[#C4BCB4] dark:text-zinc-600 ${shake ? 'animate-[shake_0.18s]' : ''}`}
            style={{ wordBreak: 'keep-all', fontSize: size }}
        >
            {words.map((group, gi) => group.space ? (
                <span key={`s${gi}`} className={group.i < index ? 'text-emerald-500' : ''}> </span>
            ) : (
                <span key={gi} className="inline-block whitespace-nowrap">
                    {group.units.map(({ unit, i }) => {
                        const done = i < index;
                        const current = i === index;
                        const bad = unitErrors[i];
                        return (
                            <span
                                key={i}
                                className={[
                                    'relative',
                                    done ? 'text-emerald-600 dark:text-emerald-400' : '',
                                    current ? 'rounded-md bg-primary/15 text-[#1C1815] dark:text-zinc-100' : '',
                                    bad ? 'underline decoration-red-400 decoration-2 underline-offset-4' : '',
                                ].join(' ')}
                            >
                                {current && <span className="absolute -left-[2px] top-[0.22em] h-[1.15em] w-[2.5px] animate-pulse rounded-full bg-primary" />}
                                {unit.text}
                            </span>
                        );
                    })}
                </span>
            ))}
        </div>
    );
});

/**
 * Free / exam display: the reference passage sits in its own panel like a printed
 * sheet. The current word is boxed; once the typist moves past a word it turns green
 * (right) or red (wrong), the way a typing test marks it afterwards.
 */
/**
 * Only `lines` lines of the passage show at once (two by default): the line being typed and the next
 * one as a preview. As the caret reaches a new line the text slides up by one line,
 * the way Monkeytype scrolls — so the eye stays in one place instead of hunting down
 * a wall of text.
 */
export const FreeSheet = memo(function FreeSheet({ session, sheetLook = false, size = 28, lines = 2 }) {
    const { words, index } = session;
    const innerRef = useRef(null);
    const activeRef = useRef(null);
    const [lineHeight, setLineHeight] = useState(0);
    const [offset, setOffset] = useState(0); // px the inner text is slid up

    // After each word change, slide so the active word's line is the top visible row.
    useLayoutEffect(() => {
        const inner = innerRef.current;
        if (!inner) return;
        const lh = parseFloat(getComputedStyle(inner).lineHeight) || 0;
        setLineHeight(lh);
        const top = activeRef.current ? activeRef.current.offsetTop : 0;
        setOffset(top);
    }, [index, words, size]);

    const viewportHeight = lineHeight ? lineHeight * lines : size * 1.7 * lines;

    return (
        <div
            dir="ltr"
            className={sheetLook
                ? 'rounded-2xl border border-dashed border-[#D8D2CB] bg-[#FBFAF8] px-6 py-4 text-[#2C2722] dark:border-zinc-700 dark:bg-zinc-950/40 dark:text-zinc-200'
                : 'text-[#5A534C] dark:text-zinc-400'}
        >
            <div className="relative overflow-hidden" style={{ height: viewportHeight }}>
                <div
                    ref={innerRef}
                    className="typing-text font-hind leading-[1.7] tracking-wide"
                    style={{ fontSize: size, transform: `translateY(${-offset}px)`, transition: 'transform 0.25s ease', wordBreak: 'keep-all' }}
                >
                    {words.map((word, i) => {
                        const past = i < index;
                        const current = i === index;
                        const correct = past && freeWordCorrect(session, i);
                        const wrong = past && !correct;
                        return (
                            <span key={i} ref={current ? activeRef : null} className="inline-block whitespace-nowrap">
                                <span
                                    className={[
                                        'rounded px-0.5',
                                        correct ? 'text-emerald-600 dark:text-emerald-400' : '',
                                        wrong ? 'text-red-500 line-through decoration-red-400/70' : '',
                                        current ? 'bg-primary/15 text-[#1C1815] dark:text-zinc-100' : '',
                                    ].join(' ')}
                                >
                                    {word}
                                </span>
                                {i < words.length - 1 ? ' ' : ''}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

/**
 * The line the typist is building in free mode — shows what they have actually typed
 * for the current word, green if it already matches, red if it has diverged.
 */
export const FreeInputLine = memo(function FreeInputLine({ session, isBn, selected = false, onSelectAll, size = 26, offTrack = null, raw = null }) {
    const i = session.index;
    const typed = session.rendered[i] || '';
    const target = session.words[i] || '';
    // `offTrack` comes from the keystroke check when the caller has it; text prefix otherwise.
    const diverged = !!typed && (offTrack ?? !target.normalize('NFC').startsWith(typed.normalize('NFC')));
    // Fixed height and line-height: the box must not grow when the first letter
    // replaces the smaller placeholder, or everything below it jumps.
    return (
        <div
            dir="ltr"
            onDoubleClick={() => typed && onSelectAll?.()}
            className={`typing-text relative flex items-center gap-1 overflow-hidden rounded-2xl border-2 bg-white px-5 transition-colors dark:bg-zinc-900 ${diverged ? 'border-red-300 dark:border-red-900/60' : 'border-primary/30 dark:border-primary/30'}`}
            style={{ fontSize: size, lineHeight: 1.4, height: Math.round(size * 2.2) }}
        >
            <span className={[
                'whitespace-pre font-hind rounded',
                selected && typed ? 'bg-primary/25 px-0.5' : '',
                diverged ? 'text-red-500' : 'text-[#1C1815] dark:text-zinc-100',
            ].join(' ')}>{typed}</span>
            {!selected && <span className="inline-block h-[1.1em] w-[2.5px] flex-none animate-pulse rounded-full bg-primary" />}
            {!typed && (
                <span className="pointer-events-none absolute inset-y-0 left-[calc(1.25rem+10px)] flex items-center font-hind text-[#B3ABA3] dark:text-zinc-600" style={{ fontSize: '0.7em' }}>
                    {isBn ? 'এখানে টাইপ করুন…' : 'type here…'}
                </span>
            )}
            {/* Phonetic layouts: the letters actually typed, like Avro's own preview. */}
            {raw ? (
                <span className="pointer-events-none absolute inset-y-0 right-5 flex items-center font-mono tracking-wide text-[#B3ABA3] dark:text-zinc-500" style={{ fontSize: '0.55em' }}>{raw}</span>
            ) : null}
        </div>
    );
});

/**
 * Phonetic layouts (Avro): the current word written as the letters that type it,
 * e.g. `a m i` for আমি — letters already typed in green, the next one marked. The
 * learner sees the Bangla word and how to spell it at the same time.
 */
export const RomanHint = memo(function RomanHint({ session, layout, kind }) {
    let letters = [];
    let done = 0;
    if (kind === 'strict') {
        const unit = session.units[session.index];
        if (!unit) return null;
        const wordIndex = unit.wordIndex;
        const wordUnits = session.units.map((u, i) => ({ u, i })).filter(({ u }) => !u.space && u.wordIndex === wordIndex);
        wordUnits.forEach(({ u, i }) => {
            const keys = (u.options[0] || []).map((s) => layout.strokeLabel(s));
            if (i < session.index) done += keys.length;
            else if (i === session.index) done += session.pressed.length;
            letters = letters.concat(keys);
        });
        if (unit.space) done = letters.length;
    } else {
        const word = session.words[session.index];
        if (!word) return null;
        letters = layout.encodeWord(word).flatMap((u) => (u.options[0] || []).map((s) => layout.strokeLabel(s)));
        done = Math.min(letters.length, (session.buffers[session.index] || []).length);
    }
    if (!letters.length) return null;
    return (
        <span dir="ltr" className="flex items-center gap-0.5 rounded-full border border-[#EEE9E4] px-3 py-1 font-mono text-[14px] dark:border-zinc-800">
            {letters.map((ch, i) => (
                <span
                    key={i}
                    className={[
                        'rounded px-[3px] text-[14px] font-semibold leading-tight',
                        i < done ? 'text-emerald-600 dark:text-emerald-400' : '',
                        i === done ? 'bg-primary text-white' : '',
                        i > done ? 'text-[#A39B93] dark:text-zinc-500' : '',
                    ].join(' ')}
                >
                    {ch}
                </span>
            ))}
        </span>
    );
});
